import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

type NavItem = { to: string; label: string };

const NAV_ITEMS: NavItem[] = [
  { to: '/idea-pad', label: 'Idea Pad' },
  { to: '/planner', label: 'Planner' },
  { to: '/ai-tools', label: 'AI Tools' },
];

// Cheap deterministic initials from the user's email.
function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (local.slice(0, 2) || 'MK').toUpperCase();
}

export default function TopNav() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [referOpen, setReferOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the profile menu when clicking outside it.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Close menus on route change so nothing lingers across navigations.
  useEffect(() => {
    setMenuOpen(false);
    setReferOpen(false);
  }, [location.pathname]);

  const referralCode = user ? user.sub.slice(0, 8).toUpperCase() : 'GUEST';
  const referralLink = `https://maximized.kids/join?ref=${referralCode}`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — non-fatal */
    }
  }

  function handleSignOut() {
    signOut();
    navigate('/login');
  }

  return (
    <>
      <header className="portal-topnav">
        {/* Logo → dashboard (the authenticated home, not the marketing site) */}
        <Link to="/dashboard" className="brand" aria-label="Maximized.Kids dashboard">
          <img src="/portal/assets/logo.svg" alt="Maximized.Kids" />
        </Link>

        {user && (
          <nav className="primary-nav" aria-label="Primary">
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => 'nav-link' + (isActive ? ' is-active' : '')}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="actions">
          {user ? (
            <div className="profile-wrap" ref={menuRef}>
              <button
                type="button"
                className="avatar-btn"
                onClick={() => setMenuOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Open profile menu"
              >
                {initialsFromEmail(user.email)}
              </button>

              {menuOpen && (
                <div className="profile-menu" role="menu">
                  <div className="profile-head">
                    <div className="profile-email">{user.email}</div>
                    <div className="profile-sub">Parent account</div>
                  </div>

                  <MenuItem icon={<UserIcon />} label="Account" onClick={() => navigate('/account')} />
                  <MenuItem icon={<GearIcon />} label="Settings" onClick={() => navigate('/settings')} />

                  <button
                    type="button"
                    role="menuitem"
                    className="menu-item refer-item"
                    onClick={() => { setMenuOpen(false); setReferOpen(true); }}
                  >
                    <span className="menu-icon"><CommunityIcon /></span>
                    <span className="menu-label">Refer a friend</span>
                  </button>

                  <MenuItem
                    icon={<ExternalIcon />}
                    label="View public site"
                    onClick={() => window.open('/', '_blank', 'noopener,noreferrer')}
                  />

                  <div className="menu-divider" />

                  <MenuItem icon={<SignOutIcon />} label="Sign out" onClick={handleSignOut} muted />
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="signin-link">Sign in</Link>
          )}
        </div>
      </header>

      {referOpen && (
        <div
          className="refer-backdrop"
          onClick={() => setReferOpen(false)}
          role="presentation"
        >
          <div
            className="refer-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="refer-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="refer-head">
              <div className="refer-icon"><CommunityIcon /></div>
              <div>
                <h2 id="refer-title">Refer a friend</h2>
                <p>We&rsquo;re looking to grow this with families who share our mindset around active involvement and positive development.</p>
              </div>
            </div>

            <label className="refer-label" htmlFor="refer-link">Your referral link</label>
            <div className="refer-row">
              <input id="refer-link" readOnly value={referralLink} />
              <button type="button" className="copy-btn" onClick={handleCopy}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            <div className="refer-actions">
              <a
                className="share-btn"
                href={`mailto:?subject=${encodeURIComponent('Try Maximized.Kids')}&body=${encodeURIComponent('Check this out: ' + referralLink)}`}
              >
                Email
              </a>
              <a
                className="share-btn"
                href={`sms:?&body=${encodeURIComponent('Check out Maximized.Kids: ' + referralLink)}`}
              >
                Text
              </a>
              <button type="button" className="close-btn" onClick={() => setReferOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

type MenuItemProps = {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  muted?: boolean;
};

function MenuItem({ icon, label, onClick, muted }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={'menu-item' + (muted ? ' is-muted' : '')}
      onClick={onClick}
    >
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
    </button>
  );
}

/* ---------- Inline SVG icons (no extra deps) ---------- */

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function CommunityIcon() {
  // Three-people "Users" glyph — universal shorthand for community / group.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
